import { dryFetch } from '../../utils/dryFetch';
import { getOwnerID } from '../../utils/DryCookies';
// import track from '../utils/trackers';
import {
  hydrateNotesClientDatabase,
  writeSystemValue,
  writeNoteToClientDatabase,
} from '../offline/';
import logger from '../../logger';
import dryEditorClass from '../../views/editor/DryEditorClass';

export const fetchNotesFromCloudPaginated = async () => {
  const ownerId = getOwnerID();
  const LIMIT = 500;
  let page = 1;
  let hasNextPage = true;
  const allNotes = [];

  while (hasNextPage) {
    const results = await dryFetch({
      name: 'fetchNotesPaginated',
      path: `/v2/notes/${ownerId}?page=${page}&limit=${LIMIT}`,
      timeout: 60000,
    });

    const pageNotes = (results.notes ?? []).map((note) => ({ ...note, editable: false }));
    allNotes.push(...pageNotes);

    hasNextPage = results.pages?.has_next_page ?? false;
    page += 1;
  }

  writeSystemValue({
    name: 'notesLastSynced',
    utcDateTime: new Date().toISOString(),
  });

  if (allNotes.length) {
    const data = await hydrateNotesClientDatabase({ ownerId, notes: allNotes });
    logger.info(data, 'fetchNotesFromCloudPaginated: wrote data to indexedDB');
  }

  const notesObject = {};
  allNotes.forEach((note) => {
    notesObject[note.note_id] = note;
  });

  logger.info({ count: allNotes.length }, 'fetchNotesFromCloudPaginated: done');
  return notesObject;
};

export const fetchNotesFromCloud = async ({ dateTimeSynced = null } = {}) => {
  // track.startTime('fetchNotesFromCloud');
  const ownerId = getOwnerID();
  // dispatch(requestNotes());
  const options = !!dateTimeSynced
    ? {
        method: 'POST',
        body: JSON.stringify({
          dateTimeSynced,
        }),
      }
    : null;

  try {
    const results = await dryFetch({
      name: 'fetchNotes',
      path: `/notes/${ownerId}`,
      options,
      timeout: 0,
    });

    // const completedTime = track.endTime('fetchNotes');
    // logger.info({
    //   ownerId,
    //   time: completedTime,
    // }, 'fetchNotes time');
    // track.time({
    //   category: 'notes',
    //   name: 'fetch_notes',
    //   value: completedTime,
    //   label: '',
    // });
    let notesList = [];

    // Write the synced time, even without notes returned
    writeSystemValue({
      name: 'notesLastSynced',
      utcDateTime: results.utcDateTime,
    });

    // dispatch(setHasLocalDatabaseContent());

    if (results.notes) {
      notesList = results.notes.map((noteObject) => {
        return Object.assign({}, noteObject, {
          editable: false,
        });
      });
    }

    // Save to IndexedDB
    if (notesList.length) {
      const data = await hydrateNotesClientDatabase({
        ownerId,
        notes: notesList,
      });
      // const normalizedNotes = normalize(data, notesListSchema);

      // if (dateTimeSynced) {
      //   dispatch(receiveSyncedNotes(normalizedNotes));
      // } else {
      //   dispatch(receiveNotes(normalizedNotes));
      // }
      logger.info(data, 'wrote data to indexedDB database');
      
      // Convert array to dictionary format expected by state machine
      const notesObject = {};
      notesList.forEach((note) => {
        notesObject[note.note_id] = note;
      });
      return notesObject;
    }

    // Return empty object if no notes (instead of undefined or empty array)
    logger.info('No notes found, returning empty object');
    return {};
  } catch (error) {
    logger.error(error, 'fetchNotes');
    throw error;
  }
};

export const fetchNoteFromCloud = async ({ noteId }) => {
  logger.debug(noteId, 'fetchNote noteId');
  try {
    const results = await dryFetch({
      name: 'fetchNote',
      path: `/note/${noteId}`,
    });
    logger.info('notesActions: fetchNote - updateNote');

    // Update note document in the notes list with the newly attached files from uploadFiles
    if (results.note) {
      // dispatch(updateNote(results.note));
      logger.info('notesActions: fetchNote - saveNote');
    } else if (results.failure) {
      logger.error(
        results.failure,
        'notesActions: fetchNote - failed to fetch'
      );
    } else {
      logger.error(results, 'notesActions: fetchNote - failed to fetch');
    }
    return results.note;
  } catch (error) {
    logger.error(error, 'notesActions: fetchNote - catch error');
    return error;
  }
};

// Fetch a SINGLE note item
export const readNoteFromCloud = (noteID) => {
  logger.debug(noteID, 'readNoteFromCloud noteID');
  return dryFetch({
    name: 'readNoteFromCloud',
    path: `/note/${noteID}`,
  })
    .then((json) => {
      logger.info('notesActions: readNoteFromCloud - updateNote');

      // Update note document in the notes list with the newly attached files from uploadFiles
      if (json.note) {
        // dispatch(updateNote(json.note));
        logger.info('notesActions: readNoteFromCloud - saveNote');
      } else if (json.failure) {
        logger.error(
          json.failure,
          'notesActions: readNoteFromCloud - failed to fetch'
        );
      } else {
        logger.error(json, 'notesActions: readNoteFromCloud - failed to fetch');
      }
    })
    .catch((error) => {
      logger.error(error, 'notesActions: readNoteFromCloud - catch error');
      throw error;
    });
};

/*
  noteObject:
  {
    'title': '[your title here]',
    'document': '[your body here]',
    'folder': '1' // Notebook ID
    'labels': [, 2, 3] // Array of label ID's
  }
*/
export const createNote = ({
  noteObject,
  selectedAreasOfFocusCollection,
  openNotesLength,
  isSelectedAreaOfFocus = false,
}) => {
  const ownerID = getOwnerID();
  noteObject.owner_id = ownerID;

  const body = { ...noteObject };
  const aofCollectionId = selectedAreasOfFocusCollection?.area_of_focus_collection_id;
  if (aofCollectionId) {
    body.area_of_focus_collection_id = aofCollectionId;
  }

  return dryFetch({
    name: 'createNote',
    path: `/note`,
    options: {
      method: 'POST',
      body: JSON.stringify(body),
    },
  })
    .then((json) => {
      return json;
    })
    .catch((error) => {
      logger.error(error);
      throw error;
    });
};

export const saveNoteCloud = async ({ noteObject }) => {
  const currentDocJSON = noteObject.note_body;
  const patch = dryEditorClass.getDocPatch(currentDocJSON);
  const PATCH_SIZE_THRESHOLD = 0.8;

  const shouldUseDelta =
    patch &&
    patch.length > 0 &&
    JSON.stringify(patch).length < JSON.stringify(currentDocJSON).length * PATCH_SIZE_THRESHOLD;

  if (shouldUseDelta) {
    try {
      const deltaResult = await dryFetch({
        name: 'saveNotePatch',
        path: `/note/${noteObject.note_id}/patch`,
        options: {
          method: 'PUT',
          body: JSON.stringify({
            patch,
            baseVersion: dryEditorClass._confirmedVersion,
            note_title: noteObject.note_title,
            note_snippet: noteObject.note_snippet,
            cursorPosition: noteObject.cursorPosition,
            notebook_id: noteObject.notebook_id,
          }),
        },
      });

      dryEditorClass.confirmSave(currentDocJSON, deltaResult.doc_version, noteObject.note_id);
      const updatedNote = { ...noteObject, doc_version: deltaResult.doc_version };
      writeNoteToClientDatabase({ noteObject: updatedNote });
      logger.debug({ note_id: noteObject.note_id, doc_version: deltaResult.doc_version }, 'saveNoteCloud: delta save');
      return updatedNote;
    } catch (error) {
      // Version mismatch or patch error — fall through to full-doc save
      logger.warn({ note_id: noteObject.note_id, status: error.status }, 'saveNoteCloud: delta failed, falling back to full save');
    }
  }

  // Full-doc save (original path / fallback)
  const noteToSave = {
    note_title: noteObject.note_title,
    label_ids: noteObject.label_ids ? noteObject.label_ids : [],
    note_body: currentDocJSON,
    notebook_id: noteObject.notebook_id,
    owner_id: noteObject.owner_id,
    app_created_with: noteObject.app_created_with,
    note_snippet: noteObject.note_snippet,
    cursorPosition: noteObject.cursorPosition,
  };

  try {
    const json = await dryFetch({
      name: 'saveNote',
      path: `/note/${noteObject.note_id}`,
      options: {
        method: 'PUT',
        body: JSON.stringify(noteToSave),
      },
    });

    dryEditorClass.confirmSave(currentDocJSON, json.note.doc_version ?? 0, noteObject.note_id);
    writeNoteToClientDatabase({ noteObject: json.note });
    logger.debug({ note_id: noteObject.note_id }, 'saveNoteCloud: full save');
    return json.note;
  } catch (error) {
    logger.error(error);
    throw error;
  }
};
